/* eslint-disable no-case-declarations */
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react'
import { refreshTokenAction } from 'feature/auth/action/refreshTokenAction'
import { AuthError, authActions } from 'feature/auth/redux/authSlice'
import { RootState } from 'store/types'
import { apiPath } from 'transport/apiVariables'

// готовим baseQuery для RTK Query
export const baseQuery = fetchBaseQuery({
  baseUrl: apiPath,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.authInfo.accessToken
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    return headers
  },
  credentials: 'include',
})

type GetBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
>

let refreshTokenPromise: Promise<boolean> | null = null

/*
  перехватываем ошибки и обрабатываем только в RTK Query
  сами эффекты после ошибок выполняем в ErrorHandlerLayout
  (редирект, вывод уведомлений и т.п.)
*/
export const getBaseQueryAuth: GetBaseQuery = async (
  args,
  api,
  extraOptions,
) => {
  // Если access token отсутствует, но есть refresh token —
  // делаем refresh ДО запроса, чтобы избежать лишнего 401 → refresh → retry
  const state = api.getState() as RootState
  const requestUrl = typeof args === 'string' ? args : args.url
  const isNoSessionEndpoint = requestUrl.startsWith('/v1/company/email/login/')
  if (
    !isNoSessionEndpoint &&
    !state.auth.authInfo.accessToken &&
    state.auth.authInfo.refreshToken
  ) {
    if (!refreshTokenPromise) {
      refreshTokenPromise = refreshTokenAction().finally(() => {
        refreshTokenPromise = null
      })
    }
    const preRefreshResult = await refreshTokenPromise
    if (!preRefreshResult) {
      api.dispatch(authActions.setAuthInfo({ error: AuthError.tokenExpired }))
      return { error: { status: 401, data: 'Token refresh failed' } }
    }
  }

  let result = await baseQuery(args, api, extraOptions)

  switch (result?.error?.status) {
    // обработка истекшего токена (access token мог истечь между запросами)
    case 401:
      if (isNoSessionEndpoint) break
      if (!refreshTokenPromise) {
        refreshTokenPromise = refreshTokenAction().finally(() => {
          refreshTokenPromise = null
        })
      }

      const refreshResult = await refreshTokenPromise

      if (refreshResult) {
        result = await baseQuery(args, api, extraOptions)
      } else {
        const hasRefreshToken = (api.getState() as RootState).auth.authInfo
          .refreshToken
        if (hasRefreshToken) {
          api.dispatch(
            authActions.setAuthInfo({ error: AuthError.tokenExpired }),
          )

          result = {
            error: {
              status: 401,
              data: 'Ошибка 401: Сессия истекла.',
            },
          }
        }
      }

      break
    // обработка доступ запрещен
    // (например заходим на страницу без полной регистрации, но с токенами)
    case 403: {
      const token = (api.getState() as RootState).auth.authInfo.accessToken
      if (token) {
        api.dispatch(authActions.setAuthInfo({ error: AuthError.accessDenied }))
      }
      result = {
        error: {
          status: 403,
          data: result?.error?.data || 'Ошибка 403: Доступ запрещен.',
        },
      }
      break
    }
    // обработка забаненного пользователя
    case 423:
      api.dispatch(
        authActions.setAuthInfo({
          error:
            //@ts-ignore
            result?.error?.data.context.status === 'AGREEMENT_PPD_REVOKED'
              ? AuthError.revokePersonalData
              : AuthError.userWasBlock,
        }),
      )
      result = {
        error: {
          status: 423,
          data: 'Ошибка 423: Учетная запись заблокирована.',
        },
      }
      break
  }

  return result
}
